import React, { useEffect } from 'react';
import { Navigate, useLocation, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useBusiness } from '../contexts/BusinessContext';

/**
 * Role-based Route Guard
 * Blocks rendering until role is confirmed, then checks if user has required role
 */
const RequireRole = ({ allowedRoles, children }) => {
  const { user, loading: authLoading } = useAuth();
  const { activeBusiness } = useBusiness();
  const location = useLocation();
  const { businessId } = useParams();
  
  // Validate businessId matches user's access
  useEffect(() => {
    if (!authLoading && businessId && user) {
      const userBusinessesStr = localStorage.getItem('user_businesses');
      if (userBusinessesStr) {
        try {
          const userBusinesses = JSON.parse(userBusinessesStr);
          const hasAccess = userBusinesses.some(b => b.id === businessId);
          if (!hasAccess) {
            console.error('[RequireRole] User does not have access to businessId:', businessId);
          }
        } catch (e) {
          console.error('[RequireRole] Failed to parse user businesses:', e);
        }
      }
    }
  }, [authLoading, businessId, user]);

  // Determine user role
  const getUserRole = () => {
    if (user?.role) {
      const role = user.role.toUpperCase();
      if (role === 'BUSINESS_OWNER') return 'OWNER';
      if (role === 'FIELD_STAFF') return 'STAFF';
      return role;
    }
    
    if (activeBusiness?.user_role) {
      const r = activeBusiness.user_role.toUpperCase();
      return r === 'FIELD_STAFF' ? 'STAFF' : r;
    }
    if (activeBusiness?.role) {
      const r = activeBusiness.role.toUpperCase();
      return r === 'FIELD_STAFF' ? 'STAFF' : r;
    }
    
    return null;
  };

  const userRole = getUserRole();

  // Debug logging (temporary - remove after fix verified)
  useEffect(() => {
    console.log('[RequireRole Debug]', {
      allowedRoles,
      userRole,
      userFromAuth: user?.role,
      authLoading,
      pathname: location.pathname,
      businessId,
      timestamp: new Date().toISOString()
    });
  }, [allowedRoles, userRole, user, authLoading, location.pathname, businessId]);

  // CRITICAL: NO redirects while auth is loading
  // Wait for auth to complete before making ANY routing decisions
  if (authLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-gray-200 border-t-blue-600 rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading authentication...</p>
        </div>
      </div>
    );
  }
  
  // Auth has completed - now check if role could be determined
  if (userRole === null) {
    console.error('[RequireRole] Role could not be determined after auth loaded', {
      user,
      activeBusiness,
      userRole: user?.role,
      businessRole: activeBusiness?.user_role || activeBusiness?.role
    });
    
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center max-w-md mx-auto p-6">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Session Error</h3>
          <p className="text-gray-600 mb-4">
            Your session could not be verified. Please login again.
          </p>
          <button
            onClick={() => {
              localStorage.removeItem('access_token');
              localStorage.removeItem('refresh_token');
              localStorage.removeItem('active_business');
              localStorage.removeItem('selectedBusinessId');
              localStorage.removeItem('user');
              window.location.href = '/login';
            }}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Return to Login
          </button>
        </div>
      </div>
    );
  }

  // Check if user has required role
  const hasRequiredRole = allowedRoles.includes(userRole);

  if (!hasRequiredRole) {
    console.warn(`[RequireRole] Access denied - User role "${userRole}" not in allowed roles:`, allowedRoles);
    
    // Redirect to correct dashboard based on user's actual role
    if (userRole === 'OWNER' && businessId) {
      return <Navigate to={`/business/${businessId}/dashboard/owner`} replace />;
    } else if (userRole === 'STAFF' && businessId) {
      return <Navigate to={`/business/${businessId}/dashboard/staff`} replace />;
    } else if (userRole === 'ADMIN' && businessId) {
      return <Navigate to={`/business/${businessId}/dashboard/owner`} replace />;
    } else {
      // No valid business context - redirect to login
      return <Navigate to="/login" state={{ from: location }} replace />;
    }
  }

  // Role authorized - render children
  return <>{children}</>;
};

export default RequireRole;
